/**
 * useNotificationRealtime — toast + badge refresh on new in-app notifications.
 *
 * Subscribes to Supabase Realtime postgres_changes INSERT on public.notifications
 * for the current user (channel filtered by user_id; RLS additionally scopes rows
 * to the user). On an insert for the *current* workspace it:
 *   (a) fires a toast (title = notification title, body = description, with a
 *       "View" action that deep-links when deep_link is present), and
 *   (b) invalidates the workspace-scoped unread-count + list queries so the bell
 *       badge updates instantly.
 *
 * Mount once, in DashboardLayout, after the auth + workspace providers. Matches
 * the existing realtime pattern in useRealtimeCampaigns.ts
 * (supabase.channel(...).on('postgres_changes', ...).subscribe()).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/contexts/WorkspaceContext';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { captureError } from '@/lib/sentry';
import type { Notification } from '@/types/notifications';

export function useNotificationRealtime(): void {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const userId = user?.id;
  const wsId = currentWorkspace?.id;

  useEffect(() => {
    if (!userId || !wsId) return;

    // One channel per user; RLS scopes rows to the user, the filter narrows the
    // server-side stream to this user's notifications only.
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          try {
            const n = payload.new as Notification;

            // Only toast for the current workspace to avoid cross-workspace noise.
            // The badge for other workspaces refreshes when the user switches
            // (the query re-runs on workspace change).
            if (n.workspace_id !== wsId) return;

            // Refresh the badge + list for the current workspace immediately.
            qc.invalidateQueries({
              queryKey: queryKeys.notifications.unreadCount(wsId, userId),
            });
            qc.invalidateQueries({
              queryKey: queryKeys.notifications.list(wsId, userId),
            });

            toast({
              title: n.title,
              description: n.body ?? undefined,
              // Only offer "View" for app-relative paths (open-redirect guard).
              action: (n.deep_link && n.deep_link.startsWith('/') && !n.deep_link.startsWith('//'))
                ? (
                    <ToastAction
                      altText="View"
                      onClick={() => navigate(n.deep_link as string)}
                    >
                      View
                    </ToastAction>
                  )
                : undefined,
            });
          } catch (err) {
            captureError(err as Error, { feature: 'notifications-realtime' });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, wsId, qc, toast, navigate]);
}
